"use client";
import React, { useState } from "react";
import { Form, Input, Checkbox, notification } from "antd";
import { submitLeadForm } from "@/lib/submit-form";
import { Constant } from "@/constants/constants";
import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaCommentDots,
  FaLock,
} from "react-icons/fa";

const { TextArea } = Input;

const HeroLeadForm = ({
  formLabel = "Service Page — Hero Form",
  badge = "Free Consultation",
  heading = "Let's Bring Your Book to Life",
  subheading = "Tell us about your project — we'll get back within 24 hours.",
  submitText = "Get a Quote",
  confidentialText = "Your information is 100% safe & confidential",
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    const res = await submitLeadForm(values, formLabel);
    if (res.ok) {
      window.location.assign("/thankyou");
      return;
    }
    setSubmitting(false);
    notification.error({
      message: "Could not send your message",
      description: res.message,
    });
  };

  // Modern, minimal input styling shared across fields.
  const fieldClass =
    "!h-12 !rounded-xl !bg-slate-50 hover:!bg-white !border !border-slate-200 hover:!border-primary focus-within:!border-primary focus-within:!bg-white !shadow-none focus-within:!shadow-[0_0_0_3px_rgba(30,167,219,0.15)] transition-all duration-200";

  const IconInput = ({ prefix, placeholder, ...props }) => (
    <Input
      prefix={<span className="text-slate-400 text-base mr-2">{prefix}</span>}
      placeholder={placeholder}
      className={`${fieldClass} !text-slate-800 !text-[15px]`}
      {...props}
    />
  );

  return (
    <div className="hero-lead-form relative w-full max-w-md">
      {/* soft glow behind the card */}
      <div className="absolute -inset-1 bg-gradient-to-tr from-brand-600/25 via-gold/20 to-brand-600/25 rounded-[28px] blur-xl opacity-70" />

      <div className="relative bg-white rounded-[24px] shadow-editorial border border-white/60 overflow-hidden">
        {/* Gradient header */}
        <div className="bg-gradient-to-br from-brand-800 to-brand-600 px-6 sm:px-8 pt-7 pb-8 text-white">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider bg-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-gold-soft animate-pulse" />
            {badge}
          </span>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold leading-tight">
            {heading}
          </h2>
          <p className="mt-1 text-white/90 text-sm">{subheading}</p>
        </div>

        {/* Form body */}
        <div className="px-6 sm:px-8 py-7 -mt-3 bg-white rounded-t-[24px] relative">
          <Form
            form={form}
            name="hero_lead_form"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="name"
              rules={[
                { required: true, message: "Please input your Name!" },
                { min: 2, message: "Name must be at least 2 characters!" },
              ]}
              className="!mb-4"
            >
              <IconInput prefix={<FaUser />} placeholder="Your Name" />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { type: "email", message: "The input is not valid E-mail!" },
                { required: true, message: "Please input your E-mail!" },
              ]}
              className="!mb-4"
            >
              <IconInput
                prefix={<FaEnvelope />}
                placeholder="Your Email Address"
              />
            </Form.Item>

            <Form.Item
              name="phone"
              rules={[
                {
                  pattern: /^\+?[\d\s\-\(\)]+$/,
                  message: "Please enter a valid phone number!",
                },
              ]}
              className="!mb-2"
            >
              <IconInput
                prefix={<FaPhoneAlt />}
                placeholder="Your Phone Number"
              />
            </Form.Item>
            <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] leading-snug text-slate-500">
              By submitting your phone number, you agree to receive SMS messages from {Constant.CompanyName} about your inquiry and services. Message frequency varies; message &amp; data rates may apply. Reply HELP for help or STOP to cancel. Consent isn&apos;t required to make a purchase.
            </p>

            <Form.Item
              name="message"
              rules={[
                { required: true, message: "Please input your message!" },
              ]}
              className="!mb-5"
            >
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-slate-400 text-base z-10 pointer-events-none">
                  <FaCommentDots />
                </span>
                <TextArea
                  placeholder="Tell us what you're looking for..."
                  rows={3}
                  className="!rounded-xl !bg-slate-50 hover:!bg-white !border !border-slate-200 hover:!border-primary focus:!border-primary focus:!bg-white !shadow-none focus:!shadow-[0_0_0_3px_rgba(30,167,219,0.15)] !text-slate-800 !text-[15px] !pl-11 !pt-2.5 transition-all duration-200"
                />
              </div>
            </Form.Item>

            <Form.Item name="smsConsent" valuePropName="checked" className="!mb-5">
              <Checkbox className="items-start text-xs leading-relaxed text-slate-500 [&_.ant-checkbox]:mt-0.5">
                I agree to receive SMS messages from {Constant.CompanyName} as described above. See our{" "}
                <a href="/privacy-policy" target="_blank" className="text-primary underline">Privacy Policy</a> and{" "}
                <a href="/terms-and-conditions#sms-terms" target="_blank" className="text-primary underline">Terms & Conditions</a>.
              </Checkbox>
            </Form.Item>

            <Form.Item className="!mb-0">
              <button
                type="submit"
                disabled={submitting}
                className="group relative w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold text-[15px] bg-gradient-to-r from-brand-700 to-brand-600 shadow-[0_10px_25px_-8px_rgba(18,88,124,0.7)] hover:shadow-[0_14px_30px_-8px_rgba(18,88,124,0.85)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:hover:translate-y-0 overflow-hidden"
              >
                <span className="absolute inset-0 bg-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative">
                  {submitting ? "Sending..." : submitText}
                </span>
              </button>
            </Form.Item>
          </Form>

          <p className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
            <FaLock className="text-[10px]" />
            {confidentialText}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroLeadForm;
